/**
 * The admin console's own copy of the `/admin*` gate (SAFE-04).
 *
 * `middleware.ts` treats `/admin`, `/settings` and `/api/admin` as HARD role
 * checks that short-circuit before the permission matrix is ever consulted:
 * role must be ADMIN, otherwise `/no-access?page=admin`. `route-guard.ts`
 * ports that branch exactly (`isAdminOnlyPath`), and `RequireAuth` — which
 * wraps every routed page in `routes.tsx` — is what runs it.
 *
 * So why this component at all? Because the admin pages are the security
 * boundary, and a gate that lives only in the route table is a gate that a
 * future re-wiring can silently drop: mount `adminRoutes` one level too high,
 * outside `RequireAuth`, and five admin screens become public with no
 * compile error to warn you. This wrapper makes the denial local to the
 * pages themselves, so the answer stays "no" regardless of where the routes
 * are mounted.
 *
 * It is deliberately IDENTICAL in outcome to the router gate, never stricter:
 *
 *   - ADMIN                -> renders (the router already allowed it too)
 *   - USER                 -> `/no-access?page=admin`, the middleware's own
 *                             destination and query string
 *   - not signed in        -> `/login`, same as the middleware's step 2
 *   - session still loading-> hold with a spinner. Holding is not denying:
 *                             redirecting mid-restore would bounce every hard
 *                             refresh of an admin page through `/login`.
 *
 * Because both gates agree, a user who can open an admin page today can open
 * it after the port, and a user who cannot still cannot — which is the whole
 * of SAFE-04 for this subtree.
 */
import { Navigate, Outlet } from "react-router-dom";
import { useSession } from "@/compat/next-auth-react";
import { FullPageSpinner } from "@/components/ui/FullPageState";
import type { ReactNode } from "react";

export function AdminOnly({ children }: { children?: ReactNode }) {
  const { data: session, status } = useSession();

  if (status === "loading") return <FullPageSpinner label="Checking your access…" />;
  if (status === "unauthenticated" || !session?.user) return <Navigate to="/login" replace />;
  if (session.user.role !== "ADMIN") return <Navigate to="/no-access?page=admin" replace />;

  // No `children` = used as a react-router layout route.
  return <>{children ?? <Outlet />}</>;
}
